import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Plus, ArrowLeft, Play, Trash2, GripVertical, 
  FileSpreadsheet, Settings, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import AdminLayout from '../components/AdminLayout';
import { 
  getSlideshow, createSlide, deleteSlide, updateSlideshow 
} from '../services/api';

const SlideshowEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [slideshow, setSlideshow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingSlide, setAddingSlide] = useState(false);

  useEffect(() => {
    loadSlideshow();
  }, [id]);

  const loadSlideshow = async () => {
    try {
      const data = await getSlideshow(id);
      setSlideshow(data);
    } catch (error) {
      console.error('Error loading slideshow:', error);
      navigate('/admin/slideshows');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSlide = async (type = 'review') => {
    setAddingSlide(true);
    try {
      const newSlide = await createSlide(id, {
        slideshow_id: id,
        slide_type: type,
        title: type === 'review' ? 'Company Review' : 'New Slide',
        content: null
      });
      // Navigate to the new slide editor
      navigate(`/admin/slideshows/${id}/slides/${newSlide._id}`);
    } catch (error) {
      console.error('Error creating slide:', error);
      alert('Error creating slide');
    } finally {
      setAddingSlide(false);
    }
  };

  const handleDeleteSlide = async (slideId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this slide?')) return;
    
    try {
      await deleteSlide(slideId);
      loadSlideshow();
    } catch (error) {
      console.error('Error deleting slide:', error);
      alert('Error deleting slide');
    }
  };

  const getSlideTypeLabel = (type) => {
    const types = {
      review: 'Company Review',
      title: 'Title Slide',
      content: 'Content Slide',
    };
    return types[type] || type;
  };

  const getSlideTypeIcon = (type) => {
    switch (type) {
      case 'review':
        return <FileSpreadsheet className="w-5 h-5 text-blue-400" />;
      default:
        return <FileSpreadsheet className="w-5 h-5 text-slate-400" />;
    }
  };

  if (loading) {
    return (
      <AdminLayout currentPage="slideshows">
        <div className="text-center py-20">
          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-400">Loading slideshow...</p>
        </div>
      </AdminLayout>
    );
  }

  if (!slideshow) {
    return (
      <AdminLayout currentPage="slideshows">
        <div className="text-center py-20">
          <p className="text-slate-400">Slideshow not found</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout currentPage="slideshows">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/slideshows')}
            className="text-slate-400 hover:text-white p-2"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h2 className="text-xl font-bold text-white">{slideshow.name}</h2>
            {slideshow.description && (
              <p className="text-slate-400 text-sm">{slideshow.description}</p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate(`/admin/slideshows/${id}/present`)}
            className="border-slate-600 text-slate-300 hover:text-white"
          >
            <Play className="w-4 h-4 mr-2" />
            Present
          </Button>
        </div>
      </div>

      {/* Slides List */}
      <div className="space-y-3 mb-6">
        {slideshow.slides && slideshow.slides.length > 0 ? (
          slideshow.slides.map((slide, index) => (
            <Card 
              key={slide._id}
              className="bg-slate-900 border-slate-800 hover:border-slate-700 transition-all cursor-pointer group"
              onClick={() => navigate(`/admin/slideshows/${id}/slides/${slide._id}`)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-slate-500">
                    <GripVertical className="w-4 h-4" />
                    <span className="text-sm font-medium w-6">{index + 1}</span>
                  </div>
                  
                  <div className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center">
                    {getSlideTypeIcon(slide.slide_type)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-white">
                      {slide.title || getSlideTypeLabel(slide.slide_type)}
                    </h3>
                    <p className="text-sm text-slate-400">
                      {getSlideTypeLabel(slide.slide_type)}
                      {slide.content?.companies && ` • ${slide.content.companies.length} companies`}
                    </p>
                  </div>
                  
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-slate-400 hover:text-red-400 h-8 w-8 p-0"
                      onClick={(e) => handleDeleteSlide(slide._id, e)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    <ChevronRight className="w-5 h-5 text-slate-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="text-center py-12 bg-slate-900/50 rounded-lg border border-slate-800">
            <FileSpreadsheet className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No Slides Yet</h3>
            <p className="text-slate-400 mb-4">Add your first slide to this slideshow</p>
          </div>
        )}
      </div>

      {/* Add Slide Section */}
      <Card className="bg-slate-900/50 border-slate-800 border-dashed">
        <CardContent className="p-6">
          <h3 className="text-sm font-medium text-slate-400 mb-4">Add New Slide</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => handleAddSlide('review')}
              disabled={addingSlide}
              className="p-4 bg-slate-800 hover:bg-slate-700 rounded-lg border border-slate-700 hover:border-blue-500/50 transition-all text-left"
            >
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center mb-3">
                <FileSpreadsheet className="w-5 h-5 text-blue-400" />
              </div>
              <h4 className="font-medium text-white mb-1">Review Slide</h4>
              <p className="text-sm text-slate-400">
                Company table with judge, timer, questions & decisions
              </p>
            </button>
            
            {/* Placeholder for future slide types */}
            <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-800 opacity-50">
              <div className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center mb-3">
                <Plus className="w-5 h-5 text-slate-500" />
              </div>
              <h4 className="font-medium text-slate-500 mb-1">More Coming Soon</h4>
              <p className="text-sm text-slate-600">
                Title slides, content slides, and more
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </AdminLayout>
  );
};

export default SlideshowEditor;
